import sassVariables from 'cityads-v3/styles/_variables.scss';
import { createTheme } from '@material-ui/core/styles';

export function sassExportsToObject(sassExports, prefix) {
  let prefixRegexp = new RegExp('^' + prefix + '-');

  if (sassExports.locals) {
    return sassExportsToObject(sassExports.locals, prefix);
  }

  return Object.keys(sassExports)
    .filter(varName => varName.match(prefixRegexp))
    .reduce((exportObj, varName) => {
      exportObj[varName.replace(prefixRegexp, '')] = sassExports[varName];

      return exportObj;
    }, {})
}

/**
 * Converts sass-loader-style exports `{ a-b-c: 1, a-b-d: 2, a-e: 3, b: 4 }`
 * into JS nested objects: `{ a: { b: { c: 1, d: 2 }, e: 3 }, b: 4 }`
 */
export function sassExportsToObjectRecursive(obj, key) {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (obj.locals) {
    return sassExportsToObjectRecursive(obj.locals, key);
  }

  let keys = Object.keys(obj);
  let keyPrefixes = keys
    .reduce((acc, key) => {
      let delimPos = key.indexOf('-');

      if (delimPos > 0) {
        acc[key] = key.substr(0, delimPos);
      }

      return acc;
    }, {});
  let preliminary = keys
    .reduce((acc, key) => {
      let prefix = keyPrefixes[key];
      let value = obj[key];

      if (prefix) {
        if (!acc[prefix]) {
          acc[prefix] = {};
        }

        let postfix = key.substr(prefix.length + 1);
        acc[prefix][postfix] = value;
      } else {
        acc[key] = value;
      }
      return acc;
    }, {});

  return Object.keys(preliminary).reduce((acc, key) => {
    acc[key] = sassExportsToObjectRecursive(preliminary[key]);
    return acc;
  }, {});
}

export const breakpoints = {
  xs: 0,
  sm: 544,
  md: 768,
  lg: 992,
  xl: 1600
};

export const theme = sassExportsToObjectRecursive(sassVariables);
export const fontFamily = sassVariables.fontFamily;

export const leftMenuSmallWidth = '72px';
export const leftMenuLargeWidth = '256px';

export const muiTheme = createTheme({
  palette: {
    primary: {
      main: '#00A6EB',
      dark: '#33b7ef',
      light: '#0074a4',
      contrastText: '#ffffff'
    },
    secondary: {
      main: '#ff5d3d',
    },
    text: {
      disabled: 'rgba(0,0,0, 0.54)',
      hint: 'rgba(0,0,0, 0.54)',
    }
  },
  breakpoints: {
    values: breakpoints
  },
  typography: {
    fontFamily: 'DINWebPro, Barlow, Calibri, "Segoe UI", "Open Sans", Helvetica, sans-serif',
    h1: {
      fontSize: '6rem',
      color: 'rgba(0,0,0, 0.87)',
      lineHeight: '1.29',
      fontWeight: '350',
      letterSpacing: '-0.015625em',
    },
    h2: {
      fontSize: '3.75rem',
      color: 'rgba(0,0,0, 0.87)',
      lineHeight: '1.28',
      fontWeight: '350',
      letterSpacing: '-0.008333em',
    },
    h3: {
      fontSize: '3rem',
      color: 'rgba(0,0,0, 0.87)',
      lineHeight: '1.29',
      fontWeight: 'normal',
    },
    h4: {
      fontSize: '2.125rem',
      color: 'rgba(0,0,0, 0.87)',
      lineHeight: '1.29',
      fontWeight: 'normal',
      letterSpacing: '0.007353em',
    },
    h5: {
      fontSize: '1.5rem',
      color: 'rgba(0,0,0, 0.87)',
      lineHeight: '1.29',
      fontWeight: 'normal',
    },
    h6: {
      fontSize: '1.25rem',
      color: 'rgba(0,0,0, 0.87)',
      lineHeight: '1.4',
      fontWeight: 'normal',
      letterSpacing: '0.0075em',
    },
    body1: {
      fontSize: '1rem',
      color: 'rgba(0,0,0, 0.87)',
      fontWeight: 'normal',
      lineHeight: '1.25',
    },
    body2: {
      fontSize: '0.875rem',
      color: 'rgba(0,0,0, 0.87)',
      fontWeight: 'normal',
      lineHeight: '1.43',
      letterSpacing: '0.017857em',
    },
    subtitle1: {
      fontSize: '1rem',
      color: 'rgba(0,0,0, 0.87)',
      fontWeight: 'normal',
      lineHeight: '1.25',
      letterSpacing: '0.009375em',
    },
    subtitle2: {
      fontSize: '0.875rem',
      color: 'rgba(0,0,0, 0.87)',
      fontWeight: '500',
      lineHeight: '1.43',
      letterSpacing: '0.007143em',
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: '500',
      letterSpacing: '0.089286em',
      textTransform: 'uppercase',
    },
    caption: {
      fontSize: '0.75rem',
      color: 'rgba(0,0,0, 0.87)',
      fontWeight: '500',
      lineHeight: '1.333',
      letterSpacing: '0.033333em',
    },
    overline: {
      fontSize: '0.625em',
      color: 'rgba(0,0,0, 0.87)',
      fontWeight: '500',
      lineHeight: '2',
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
    }
  },
  overrides: {
    MuiTab: {
      root: {
        textTransform: 'initial',
        fontSize: '16px',
        fontWeight: '400',
        letterSpacing: '0',
      },
      textColorPrimary: {
        color: 'inherit',
      },
    },
    MuiButton: {
      root: {
        padding: '8px 16px 4px',
      },
      sizeSmall: {
        padding: '5px 10px 3px'
      },
      text: {
        padding: '8px 8px 4px',
        color  : '#00A6EB',
        '&:hover': {
          backgroundColor: 'rgba(0,0,0, 0.05)',
        },
      },
      textSizeSmall: {
        padding: '5px 5px 3px'
      },
      contained: {
        color          : '#00A6EB',
        backgroundColor: '#f2f2f2',
        '&:hover': {
          backgroundColor: '#e8e8e8',
        },
      },
      outlined: {
        padding: '7px 15px 3px',
      },
      outlinedSizeSmall: {
        padding: '4px 9px 2px'
      },
      startIcon: {
        marginTop: '-4px',
      },
      endIcon: {
        marginTop: '-4px',
      },
      iconSizeSmall: {
        marginTop: '-2px',
      }
    },
    MuiInput: {
      underline: {
        '&:before': {
          borderBottomColor: 'rgba(0,0,0, 0.12)',
        },
        '&:hover:not($disabled):before': {
          borderBottomColor: 'rgba(0,0,0, 0.24)',
          '@media (hover: none)': {
            borderBottomColor: 'rgba(0,0,0, 0.12)',
          },
        },
        '&$disabled:before': {
          borderBottomColor: 'rgba(35, 31, 32, 0.26)',
          borderBottomStyle: 'dotted',
        },
      },
    },
    MuiInputLabel: {
      root: {
        color: 'rgba(0,0,0, 0.54)',
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: 36
      }
    },
    MuiListItemText: {
      root: {
        color: 'rgba(0,0,0,0.87)'
      }
    },
    MuiTableRow: {
      hover: {
        '&:hover': {
          backgroundColor: '#cfecf3 !important'
        }
      }
    },
  },
  props: {
    MuiButton: {
      disableElevation: true,
    },
  },
});


export function makeStripesStyle(angle, width, colors) {
  if (colors.length === 0) {
    return {};
  }

  if (colors.length === 1) {
    return {
      backgroundColor: colors[0],
    };
  }

  const colorStopPairs = colors.map((color, index) => [
    `${color} ${index * width}px`,
    `${color} ${(index + 1) * width}px`,
  ]);
  const colorStops = [].concat(...colorStopPairs);

  return {
    backgroundImage: `repeating-linear-gradient(${angle}, ${colorStops.join(', ')})`,
  };
}

export const STRIPE_WIDTH_WIDE = 32;
